import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, useTranslate } from "@tovi/ui";
import { api } from "@/lib/api.ts";
import { useTill } from "@/lib/use-till.ts";
import { subscribeToStoreEvents } from "@/lib/events.ts";
import { drainPrintQueue } from "@/lib/print-worker.ts";

/**
 * The kitchen display (`08-pos-terminal-and-qr.md` § Order flow 6).
 *
 * **Why a screen and not a printer.** A station ticket is already written at
 * `POST /orders/{id}/items` and queued to every printer standing at that
 * station — but there is no print transport yet, so until one exists a ticket
 * reaches a print-job row and stops. This is the other half of the same
 * requirement, and it is the half that does not need hardware: the queue this
 * reads is the same one the ticket comes from, and a station with no printer
 * was always meant to be able to work off a screen. `stations.noPrintersHint`
 * says as much on the back office.
 *
 * **It shows work, not orders.** The service screen is organised around a bill
 * because a cashier is settling one; a pass is organised around what has to be
 * made next, oldest first, and a line that is already served is gone. Those are
 * different questions about the same rows, which is why this is a screen of its
 * own rather than a filter on the other.
 *
 * Nothing is computed here and nothing is cached across a refetch. An event
 * says something changed and this asks again — the same shape every other
 * realtime consumer in the till uses, and the reason a stale event cannot leave
 * a station making something that was voided.
 */

type StationTicket = {
  orderId: string;
  orderNo: string;
  tableName: string | null;
  diningStyle: string;
  createdAt: string;
  items: {
    id: string;
    name: string;
    stations: { id: string; name: string; servedAt: string | null }[];
    quantity: string;
    note: string | null;
    specifications: { name: string; price: string }[];
    toppings: { name: string; price: string }[];
    status: string;
    firedAt: string | null;
    courseNo: number;
    seatNo: number | null;
  }[];
};

type Station = { id: string; name: string };

/**
 * How long a ticket has been waiting, in whole minutes.
 *
 * The number a pass actually runs on. Rendered from a clock this component
 * ticks itself rather than from the server, because a ticket that says "4 min"
 * for twenty minutes is worse than no number at all.
 */
function waitingMinutes(since: string, now: number): number {
  return Math.max(0, Math.floor((now - new Date(since).getTime()) / 60_000));
}

export function StationScreen(): React.ReactElement {
  const t = useTranslate();
  const till = useTill();
  const queryClient = useQueryClient();
  const [stationId, setStationId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState<string | null>(null);

  // A minute is the resolution the number is shown at, so it is the rate worth
  // re-rendering at.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const printers = useQuery({
    queryKey: ["printers"],
    queryFn: () =>
      api<{ data: { id: string; role: string; stationId: string | null }[] }>(
        "/store/printers?size=100",
      ).then((r) => r.data),
    // A merchant without `STORE_SETTINGS_PRINTER_PAIRING` gets a 402 here, and
    // a pass with no printers simply prints nothing.
    retry: false,
    staleTime: 5 * 60_000,
  });

  const stations = useQuery({
    queryKey: ["stations"],
    queryFn: () => api<{ data: Station[] }>("/store/stations?size=100").then((r) => r.data),
    staleTime: 5 * 60_000,
  });

  /**
   * The print worker, running on the pass.
   *
   * Here rather than in the shell, because this is the screen a station has
   * open and the printers it drains are the ones standing at that station —
   * which is what the filter below makes true. `role === "station"` only, and
   * narrowed to the selected station while a chip is held; under "All" this is
   * a pass showing every station, so every station printer in the store is the
   * right answer and a `receipt` or `label` one still is not.
   *
   * **A claim is destructive.** `POST /store/print-jobs/claim` moves the rows
   * it selects to `printing` in the same statement that reads them, so an id
   * this screen has no business with is not a harmless extra: the job leaves
   * the counter's queue and the counter never sees it. Draining the whole
   * store is how a kitchen screen came to print the front counter's receipts
   * and kick its drawer. A receipt printer's queue belongs to the till
   * standing beside it.
   *
   * Five seconds: a ticket that reaches paper a few seconds after it reaches
   * the screen is fine, and polling faster would mostly claim nothing.
   */
  const printerIds = useMemo(
    () =>
      (printers.data ?? [])
        .filter(
          (printer) =>
            printer.role === "station" && (stationId === null || printer.stationId === stationId),
        )
        .map((printer) => printer.id),
    [printers.data, stationId],
  );

  useEffect(() => {
    if (till.till.mode === "offline" || printerIds.length === 0) return;
    let stopped = false;
    const tick = async () => {
      if (!stopped) await drainPrintQueue(printerIds);
    };
    void tick();
    const timer = setInterval(() => void tick(), 5_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [till.till.mode, printerIds]);

  const queue = useQuery({
    queryKey: ["station-queue", stationId],
    queryFn: () =>
      api<{ data: StationTicket[] }>(
        `/store/orders/station-queue?size=100${stationId ? `&stationId=${stationId}` : ""}`,
      ).then((r) => r.data),
    // Short, because a pass that is thirty seconds behind is a pass making the
    // wrong thing. The event below is what usually gets there first.
    staleTime: 5_000,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (till.till.mode === "offline") return;
    const stream = subscribeToStoreEvents((event) => {
      if (event.type === "orders.new" || event.type === "orders.updated") {
        void queryClient.invalidateQueries({ queryKey: ["station-queue"] });
      }
    });
    return () => stream.close();
  }, [till.till.mode, queryClient]);

  /**
   * Marks a line served.
   *
   * Straight to the server rather than through the offline queue, and
   * deliberately: `serve` is a statement about food that has left the pass, and
   * a queued one would let two screens disagree about what is still to make
   * while neither is wrong. A pass with no connection has a paper ticket
   * problem, not a sync problem.
   */
  async function serve(ticket: StationTicket, itemId: string): Promise<void> {
    setBusy(itemId);
    try {
      // `api()` serialises the body itself — stringifying here sends a JSON
      // *string* where the route expects an object, which is a 422 the screen
      // would otherwise swallow into "nothing happened".
      //
      // The chip names itself, because a line can reach two stations: the pass
      // finishing a plate must not close it under the larder still garnishing
      // it. On "All" there is no station speaking, and the line closes whole.
      await api(`/store/orders/${ticket.orderId}/items/${itemId}`, {
        method: "PATCH",
        body: { action: "serve", stationId, reason: null },
      });
      await queryClient.invalidateQueries({ queryKey: ["station-queue"] });
    } finally {
      setBusy(null);
    }
  }

  const tickets = useMemo(() => queue.data ?? [], [queue.data]);

  return (
    <div className="flex h-full flex-col bg-surface-sunken">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2">
        <button
          type="button"
          onClick={() => setStationId(null)}
          className={`rounded-control border px-3 py-1 text-sm ${
            stationId === null
              ? "border-primary bg-primary text-ink-on-primary"
              : "border-line text-ink-body"
          }`}
        >
          {t("pos.station.all")}
        </button>
        {(stations.data ?? []).map((station) => (
          <button
            key={station.id}
            type="button"
            onClick={() => setStationId(station.id)}
            className={`rounded-control border px-3 py-1 text-sm ${
              stationId === station.id
                ? "border-primary bg-primary text-ink-on-primary"
                : "border-line text-ink-body"
            }`}
          >
            {station.name}
          </button>
        ))}
      </div>

      {tickets.length === 0 ? (
        <p className="p-8 text-center text-ink-muted">{t("pos.station.empty")}</p>
      ) : (
        <ul className="grid flex-1 auto-rows-min grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] gap-3 overflow-y-auto p-3">
          {tickets.map((ticket) => {
            const waited = waitingMinutes(ticket.createdAt, now);
            return (
              <li
                key={ticket.orderId}
                className="flex flex-col rounded-card border border-line bg-surface"
              >
                <div className="flex items-baseline justify-between border-b border-line px-3 py-2">
                  <span className="text-sm font-semibold text-ink-body">
                    {ticket.tableName ?? ticket.orderNo}
                  </span>
                  {/* Ten minutes is where a pass starts losing a table, so it
                      is where the ticket stops looking ordinary. */}
                  <Badge tone={waited >= 10 ? "warning" : "neutral"}>
                    {t("pos.station.waiting", { minutes: String(waited) })}
                  </Badge>
                </div>

                <ul className="flex flex-1 flex-col gap-1 p-2">
                  {/* Grouped by course, because a pass works in the order the
                      room eats rather than the order things were keyed. A
                      ticket with one course shows no heading — most do. */}
                  {[...new Set(ticket.items.map((item) => item.courseNo))]
                    .sort((a, b) => a - b)
                    .flatMap((courseNo, _, courses) => [
                      courses.length > 1 ? (
                        <li
                          key={`course-${courseNo}`}
                          className="px-1 pt-1 text-xs font-medium uppercase text-ink-muted"
                        >
                          {t("pos.station.course", { number: String(courseNo) })}
                        </li>
                      ) : null,
                      ...ticket.items
                        .filter((item) => item.courseNo === courseNo)
                        .map((item) => (
                          <li key={item.id}>
                            <button
                              type="button"
                              disabled={busy === item.id}
                              onClick={() => void serve(ticket, item.id)}
                              className="w-full rounded-control border border-line px-3 py-2 text-left disabled:opacity-50"
                            >
                              <span className="flex justify-between gap-2 text-sm text-ink-body">
                                <span className="font-medium">{item.name}</span>
                                <span className="tabular-nums">×{item.quantity}</span>
                              </span>
                              {item.specifications.length > 0 || item.toppings.length > 0 ? (
                                <span className="mt-0.5 block text-xs text-ink-muted">
                                  {[...item.specifications, ...item.toppings]
                                    .map((choice) => choice.name)
                                    .join(" · ")}
                                </span>
                              ) : null}
                              {/* A note is the one thing on a ticket that is not
                            derivable from the menu, so it is the one thing
                            that must not be quiet. */}
                              {item.note ? (
                                <span className="mt-1 block text-xs font-medium text-danger">
                                  {item.note}
                                </span>
                              ) : null}
                              {/* On "All", say which stations still have this
                                  to make — the ones that have already stamped
                                  are nobody's work now. */}
                              {stationId === null &&
                              item.stations.some((station) => station.servedAt === null) ? (
                                <span className="mt-1 block text-xs text-ink-muted">
                                  {item.stations
                                    .filter((station) => station.servedAt === null)
                                    .map((station) => station.name)
                                    .join(" · ")}
                                </span>
                              ) : null}
                            </button>
                          </li>
                        )),
                    ])}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
